/**
 * 🔁 «المهام المتكررة» — تبويب جوّه شاشة «فريقي» (`/team/recurring`).
 *
 * ⚠️ **ليه تبويب مش عنصر في قائمة التنقّل:** القايمة الجانبية ثابتة لكل
 *    المستخدمين ومش بتتفلتر بالصلاحية. عنصر جديد فيها معناه إن كل عضو
 *    يشوف «المهام المتكررة» ويدوس عليها ويهبط على «مش من صلاحياتك» —
 *    وده كذب على الموظف بنفس شكل عطل الجرس الموثّق. المكان الطبيعي
 *    للقالب هو الشاشة اللي المنسقة بتدير منها فريقها.
 *
 * ⛔ فعل أساسي كحلي **واحد** في التبويب: «حفظ» وهي بتكتب، و«+ مهمة متكررة»
 *    وهي مقفولة. باقي الأزرار شبحية.
 * ⛔ ممنوع الفشل الصامت: كل فعل بيفشل بيتسجّل ERROR وبيعرض سبب السيستم
 *    زي ما هو — الواجهة ما بتصيغش رسايل من عندها.
 * ⛔ أرقام لاتينية (`fmtNum`) · الاتجاه RTL · بلا Tailwind.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "@/components/Icon";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoResultsState,
} from "@/components/states/ScreenStates";
import { errorMessageAr } from "@/api/client";
import { DEPARTMENTS, departmentLabel, listUsers, type AdminUser } from "@/api/admin";
import {
  WEEKDAYS_AR,
  createRecurrence,
  deleteRecurrence,
  lastRunLabelAr,
  listRecurrences,
  setRecurrenceActive,
  updateRecurrence,
  type Recurrence,
  type RecurrenceChecklistItem,
  type RecurrenceFrequency,
  type RecurrenceInput,
} from "@/api/recurrences";
import { fmtNum } from "@/lib/format";
import "@/styles/registry.css";

type Filter = "all" | "active" | "stopped";

const FILTERS: readonly { key: Filter; label: string }[] = [
  { key: "all", label: "الكل" },
  { key: "active", label: "شغّالة" },
  { key: "stopped", label: "موقوفة" },
];

/** مسوّدة الفورم — نفس شكل الجسم اللي السيستم بيستقبله */
type Draft = {
  title_ar: string;
  body_ar: string;
  dept: string;
  assignee_id: string;
  frequency: RecurrenceFrequency;
  days_of_week: number[];
  run_at_local: string;
  sla_hours: string;
  catchup_days: string;
  priority: "normal" | "urgent";
  checklist: RecurrenceChecklistItem[];
};

function emptyDraft(dept: string): Draft {
  return {
    title_ar: "",
    body_ar: "",
    dept,
    assignee_id: "",
    frequency: "daily",
    days_of_week: [],
    run_at_local: "09:00",
    sla_hours: "",
    catchup_days: "1",
    priority: "normal",
    checklist: [],
  };
}

function draftOf(row: Recurrence): Draft {
  return {
    title_ar: row.title_ar,
    body_ar: row.body_ar,
    dept: row.dept ?? "",
    assignee_id: row.assignee_id ?? "",
    frequency: row.frequency,
    days_of_week: [...row.days_of_week],
    run_at_local: row.run_at_local,
    sla_hours: row.sla_hours === null ? "" : String(row.sla_hours),
    catchup_days: String(row.catchup_days),
    priority: row.priority,
    checklist: row.checklist.map((c) => ({ ...c })),
  };
}

function toInput(draft: Draft): RecurrenceInput {
  return {
    title_ar: draft.title_ar.trim(),
    body_ar: draft.body_ar.trim(),
    dept: draft.dept,
    assignee_id: draft.assignee_id || null,
    priority: draft.priority,
    sla_hours: draft.sla_hours.trim() === "" ? null : Number(draft.sla_hours),
    checklist: draft.checklist,
    frequency: draft.frequency,
    days_of_week: draft.frequency === "weekly" ? draft.days_of_week : [],
    run_at_local: draft.run_at_local.trim(),
    catchup_days: Number(draft.catchup_days || 0),
  };
}

export function Recurrences({ query, myDept }: { query: string; myDept: string | null }) {
  const [rows, setRows] = useState<Recurrence[] | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  /** `null` = الفورم مقفول · `"new"` = قالب جديد · معرّف = تعديل */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** الصف اللي بيتأكد على حذفه — الحذف محتاج سبب مكتوب */
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState("");

  const [people, setPeople] = useState<AdminUser[]>([]);

  const load = useCallback(async () => {
    setFatal(null);
    try {
      const page = await listRecurrences({ perPage: 100 });
      setRows(page.rows);
    } catch (err) {
      console.error("[recurrences] تحميل القوالب فشل", err);
      setFatal(errorMessageAr(err));
      setRows(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // أعضاء القسم — للاختيار «المسؤول». فشلها **مش** بيقفل الشاشة: القالب
  // ينفع يتحفظ بلا مسؤول (طابور القسم) وده وضع صحيح مش نقص.
  const formDept = draft?.dept ?? myDept ?? "";
  useEffect(() => {
    if (!formDept) return;
    let alive = true;
    listUsers({ department: formDept, page: 1, perPage: 100 }).then(
      (res) => {
        if (alive) setPeople(res.rows);
      },
      (err: unknown) => {
        console.error("[recurrences] تحميل أعضاء القسم فشل", err);
        if (alive) setPeople([]);
      },
    );
    return () => {
      alive = false;
    };
  }, [formDept]);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  function showToast(text: string) {
    setToast(text);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }

  function noteRowError(id: string, messageAr: string) {
    setRowErrors((prev) => ({ ...prev, [id]: messageAr }));
  }

  function clearRowError(id: string) {
    setRowErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function openNew() {
    setEditing("new");
    setDraft(emptyDraft(myDept ?? (DEPARTMENTS[0]?.value ?? "")));
    setFormError(null);
  }

  function openEdit(row: Recurrence) {
    setEditing(row.id);
    setDraft(draftOf(row));
    setFormError(null);
  }

  function closeForm() {
    setEditing(null);
    setDraft(null);
    setFormError(null);
  }

  async function onSave() {
    if (!draft || saving) return;
    setSaving(true);
    setFormError(null);
    try {
      if (editing === "new") {
        const created = await createRecurrence(toInput(draft));
        showToast(`«${created.title_ar}» هتتولّد ${created.schedule_label_ar}`);
      } else {
        const current = rows?.find((r) => r.id === editing);
        if (!current) throw new Error("الصف اتغيّر");
        const saved = await updateRecurrence(current.id, toInput(draft), current.version);
        showToast(`«${saved.title_ar}» اتعدّلت — ${saved.schedule_label_ar}`);
      }
      closeForm();
      await load();
    } catch (err) {
      console.error("[recurrences] حفظ القالب فشل", err);
      setFormError(errorMessageAr(err));
    } finally {
      setSaving(false);
    }
  }

  async function onToggle(row: Recurrence) {
    if (busyId) return;
    setBusyId(row.id);
    clearRowError(row.id);
    try {
      const saved = await setRecurrenceActive(row.id, !row.is_active, row.version);
      showToast(
        saved.is_active
          ? `«${saved.title_ar}» رجعت تشتغل ${saved.schedule_label_ar}`
          : `«${saved.title_ar}» وقفت — المهام المفتوحة منها فاضلة زي ما هي`,
      );
      await load();
    } catch (err) {
      console.error(`[recurrences] تغيير حالة ${row.id} فشل`, err);
      noteRowError(row.id, errorMessageAr(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(row: Recurrence) {
    if (busyId) return;
    const reason = deleteReason.trim();
    if (reason.length < 2) {
      noteRowError(row.id, "اكتب سبب الحذف الأول");
      return;
    }
    setBusyId(row.id);
    clearRowError(row.id);
    try {
      await deleteRecurrence(row.id, reason, row.version);
      showToast(`«${row.title_ar}» اتحذفت — المهام المفتوحة منها فاضلة زي ما هي`);
      setDeleting(null);
      setDeleteReason("");
      await load();
    } catch (err) {
      console.error(`[recurrences] حذف ${row.id} فشل`, err);
      noteRowError(row.id, errorMessageAr(err));
    } finally {
      setBusyId(null);
    }
  }

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    const all = rows ?? [];
    const byFilter = all.filter((r) =>
      filter === "all" ? true : filter === "active" ? r.is_active : !r.is_active,
    );
    if (!q) return byFilter;
    return byFilter.filter((r) =>
      [r.title_ar, r.body_ar, r.schedule_label_ar].some((f) => f.toLowerCase().includes(q)),
    );
  }, [rows, filter, q]);

  const peopleById = useMemo(
    () => new Map(people.map((p) => [p.id, p.full_name_ar])),
    [people],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="sec-h">
          <span className="sec-title">
            المهام المتكررة (<span className="num">{fmtNum(visible.length)}</span>)
          </span>
          <span className="sec-note">القالب بيولّد نسخة واحدة كل يوم مستحق</span>
          {editing === null ? (
            <button
              type="button"
              className="btn sm primary"
              style={{ marginRight: "auto" }}
              onClick={openNew}
            >
              + مهمة متكررة
            </button>
          ) : null}
        </div>

        <div className="reg-tabs">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`btn sm ${filter === f.key ? "" : "ghost"}`}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {editing !== null && draft !== null ? (
          <RecurrenceForm
            draft={draft}
            onChange={setDraft}
            people={people}
            peopleLoaded={people.length > 0}
            canPickDept={editing === "new"}
            error={formError}
            saving={saving}
            onSave={() => void onSave()}
            onCancel={closeForm}
          />
        ) : null}

        {rows === null && fatal === null ? <LoadingState rows={4} /> : null}
        {fatal ? <ErrorState messageAr={fatal} onRetry={() => void load()} /> : null}

        {rows !== null && rows.length === 0 ? (
          <EmptyState
            title="مفيش مهام متكررة لسه"
            hint="اكتب قالب واحد — والسيستم هيولّد نسخته كل يوم في ميعادها"
          />
        ) : null}

        {rows !== null && rows.length > 0 && visible.length === 0 ? (
          q ? (
            <NoResultsState query={q} />
          ) : (
            <EmptyState
              title={filter === "active" ? "مفيش قالب شغّال" : "مفيش قالب موقوف"}
              hint="غيّر الفلتر عشان تشوف الباقي"
            />
          )
        ) : null}

        {visible.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ borderRadius: "0 8px 8px 0" }}>المهمة</th>
                  <th>بتتولّد إمتى</th>
                  <th>القسم</th>
                  <th>مين مسؤول</th>
                  <th>آخر نسخة</th>
                  <th style={{ borderRadius: "8px 0 0 8px" }}>إجراء</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.id} className="row-link">
                    <td style={{ minWidth: 220 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span className="t-body clip">{row.title_ar}</span>
                        {row.is_active ? null : (
                          <span className="chip ghost" style={{ alignSelf: "flex-start" }}>
                            <span className="bullet" />
                            موقوفة
                          </span>
                        )}
                        {row.warning_ar ? (
                          <span className="chip late" style={{ alignSelf: "flex-start" }}>
                            <span className="bullet" />
                            {row.warning_ar}
                          </span>
                        ) : null}
                        {rowErrors[row.id] ? (
                          <span
                            className="t-meta"
                            style={{ color: "var(--danger)" }}
                            role="alert"
                          >
                            {rowErrors[row.id]}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="t-desc">
                      {/* ⚠️ النص جاي من السيستم — <bdi> بلا dir لأن جواه عربي وأرقام */}
                      <bdi>{row.schedule_label_ar}</bdi>
                    </td>
                    <td className="t-desc">{departmentLabel(row.dept)}</td>
                    <td className="t-desc">
                      {row.assignee_id
                        ? peopleById.get(row.assignee_id) ?? "عضو من القسم"
                        : "طابور القسم"}
                    </td>
                    <td className="t-desc">
                      <bdi>{lastRunLabelAr(row)}</bdi>
                      {row.generated_count > 0 ? (
                        <>
                          {" · "}
                          <span className="num">{fmtNum(row.generated_count)}</span> نسخة
                        </>
                      ) : null}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="btn sm ghost"
                          disabled={busyId !== null || editing !== null}
                          onClick={() => openEdit(row)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          className={`btn sm ghost${busyId === row.id ? " is-busy" : ""}`}
                          disabled={busyId !== null}
                          title={
                            row.is_active
                              ? "الإيقاف بيمنع النسخ الجاية بس — المهام المفتوحة بتفضل"
                              : "التشغيل بيبدأ من النهارده، مش بيعوّض أيام التوقف"
                          }
                          onClick={() => void onToggle(row)}
                        >
                          {row.is_active ? "إيقاف" : "تشغيل"}
                        </button>
                        <button
                          type="button"
                          className="btn sm ghost"
                          disabled={busyId !== null}
                          onClick={() => {
                            setDeleting(deleting === row.id ? null : row.id);
                            setDeleteReason("");
                            clearRowError(row.id);
                          }}
                        >
                          حذف
                        </button>
                      </div>
                      {deleting === row.id ? (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <input
                            className="reg-input"
                            style={{ flex: "1 1 180px" }}
                            placeholder="سبب الحذف — بيتسجّل في السجل"
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                          />
                          <button
                            type="button"
                            className={`btn sm ghost${busyId === row.id ? " is-busy" : ""}`}
                            disabled={busyId !== null}
                            onClick={() => void onDelete(row)}
                          >
                            تأكيد الحذف
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <div
          className="t-meta"
          style={{ borderTop: "1px solid var(--line)", paddingTop: 16, lineHeight: 1.7 }}
        >
          المواعيد كلها <bdi>بتوقيت الأكاديمية (القاهرة)</bdi> — مش بتوقيت الأسرة.
          <br />
          إيقاف القالب أو حذفه بيمنع النسخ الجاية بس؛ المهام اللي اتولّدت خلاص بتفضل
          عند أصحابها لحد ما يقفلوها أو يلغوها بسبب.
        </div>
      </div>

      {toast ? (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: "50%",
            transform: "translateX(50%)",
            zIndex: 40,
          }}
        >
          <div className="toast" role="status">
            <Icon name="check" size={14} stroke="#FFFFFF" strokeWidth={2.2} />
            {toast}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────────────── الفورم ─────────────────────── */

function RecurrenceForm({
  draft,
  onChange,
  people,
  peopleLoaded,
  canPickDept,
  error,
  saving,
  onSave,
  onCancel,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
  people: AdminUser[];
  peopleLoaded: boolean;
  canPickDept: boolean;
  error: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    onChange({ ...draft, [key]: value });

  function toggleDay(day: number) {
    const has = draft.days_of_week.includes(day);
    set(
      "days_of_week",
      has ? draft.days_of_week.filter((d) => d !== day) : [...draft.days_of_week, day].sort(),
    );
  }

  const weeklyWithoutDays = draft.frequency === "weekly" && draft.days_of_week.length === 0;
  const blocked = draft.title_ar.trim().length < 2 || weeklyWithoutDays;

  return (
    <div
      className="card flat"
      style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div className="sec-h">
        <span className="sec-title">{canPickDept ? "مهمة متكررة جديدة" : "تعديل القالب"}</span>
      </div>

      <Field label="عنوان المهمة زي ما الموظف هيشوفه">
        <input
          className="reg-input"
          value={draft.title_ar}
          onChange={(e) => set("title_ar", e.target.value)}
          placeholder="تواصل مع أولياء الأمور للاطمئنان"
        />
      </Field>

      <Field label="سطر السياق (اختياري)">
        <input
          className="reg-input"
          value={draft.body_ar}
          onChange={(e) => set("body_ar", e.target.value)}
          placeholder="اللي مالهمش تواصل من أسبوع"
        />
      </Field>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Field label="القسم">
          <select
            className="reg-select"
            value={draft.dept}
            disabled={!canPickDept}
            title={canPickDept ? undefined : "القسم مابيتغيّرش بعد الإنشاء"}
            onChange={(e) => onChange({ ...draft, dept: e.target.value, assignee_id: "" })}
          >
            {DEPARTMENTS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="مين مسؤول">
          <select
            className="reg-select"
            value={draft.assignee_id}
            onChange={(e) => set("assignee_id", e.target.value)}
          >
            <option value="">طابور القسم — أول واحد يستلمها</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name_ar}
              </option>
            ))}
          </select>
          {!peopleLoaded ? (
            <span className="t-meta">
              أعضاء القسم مش متاحين دلوقتي — سيبها لطابور القسم
            </span>
          ) : null}
        </Field>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Field label="التكرار">
          <select
            className="reg-select"
            value={draft.frequency}
            onChange={(e) =>
              onChange({
                ...draft,
                frequency: e.target.value as RecurrenceFrequency,
                days_of_week: [],
              })
            }
          >
            <option value="daily">كل يوم</option>
            <option value="weekly">أيام محدّدة من الأسبوع</option>
          </select>
        </Field>

        <Field label="الساعة بتوقيت الأكاديمية">
          {/* ⚠️ `dir="ltr"` هنا آمنة: المحتوى `HH:MM` لاتيني صافي بلا حرف عربي */}
          <input
            className="reg-input"
            type="time"
            dir="ltr"
            value={draft.run_at_local}
            onChange={(e) => set("run_at_local", e.target.value)}
          />
        </Field>

        <Field label="الأولوية">
          <select
            className="reg-select"
            value={draft.priority}
            onChange={(e) => set("priority", e.target.value as "normal" | "urgent")}
          >
            <option value="normal">عادية</option>
            <option value="urgent">عاجلة</option>
          </select>
        </Field>

        <Field label="المهلة بالساعات (اختياري)">
          <input
            className="reg-input"
            inputMode="numeric"
            value={draft.sla_hours}
            onChange={(e) => set("sla_hours", e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="مثلًا 4"
          />
        </Field>

        <Field label="تعويض الأيام الفايتة">
          <select
            className="reg-select"
            value={draft.catchup_days}
            onChange={(e) => set("catchup_days", e.target.value)}
          >
            <option value="0">النهارده بس</option>
            <option value="1">يوم فايت كمان</option>
            <option value="2">يومين فايتين</option>
            <option value="3">3 أيام فايتة</option>
          </select>
        </Field>
      </div>

      {draft.frequency === "weekly" ? (
        <Field label="أيام الأسبوع">
          <div className="reg-tabs">
            {WEEKDAYS_AR.map((name, index) => (
              <button
                key={name}
                type="button"
                className={`btn sm ${draft.days_of_week.includes(index) ? "" : "ghost"}`}
                aria-pressed={draft.days_of_week.includes(index)}
                onClick={() => toggleDay(index)}
              >
                {name}
              </button>
            ))}
          </div>
          {weeklyWithoutDays ? (
            <span className="t-meta" style={{ color: "var(--danger)" }}>
              اختار يوم واحد على الأقل — القالب من غير أيام عمره ما هيشتغل
            </span>
          ) : null}
        </Field>
      ) : null}

      <ChecklistEditor
        items={draft.checklist}
        onChange={(items) => set("checklist", items)}
      />

      {error ? (
        <span className="t-meta" style={{ color: "var(--danger)" }} role="alert">
          {error}
        </span>
      ) : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          type="button"
          className={`btn primary${saving ? " is-busy" : ""}`}
          disabled={saving || blocked}
          onClick={onSave}
        >
          {saving ? "بيتحفظ…" : "حفظ"}
        </button>
        <button type="button" className="btn ghost" disabled={saving} onClick={onCancel}>
          إلغاء
        </button>
        <span className="t-meta">
          النسخة الأولى هتتولّد أول ما الساعة توصل ميعادها بتوقيت الأكاديمية.
        </span>
      </div>
    </div>
  );
}

/** قايمة بنود بسيطة — البند بيتنسخ في كل نسخة يومية «مش متعمل» */
function ChecklistEditor({
  items,
  onChange,
}: {
  items: RecurrenceChecklistItem[];
  onChange: (items: RecurrenceChecklistItem[]) => void;
}) {
  const [label, setLabel] = useState("");

  function add() {
    const text = label.trim();
    if (!text) return;
    // المفتاح داخلي بس — النص العربي هو اللي بيتعرض
    const key = `item_${items.length + 1}_${Date.now().toString(36)}`;
    onChange([...items, { key, label_ar: text, required: true }]);
    setLabel("");
  }

  return (
    <Field label="بنود القايمة (اختياري)">
      {items.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((item) => (
            <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="t-desc clip">{item.label_ar}</span>
              <button
                type="button"
                className="link"
                onClick={() => onChange(items.filter((i) => i.key !== item.key))}
              >
                شيله
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          className="reg-input"
          value={label}
          placeholder="بند زي «سجّلت نتيجة المكالمة»"
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="btn sm ghost" onClick={add}>
          إضافة بند
        </button>
      </div>
    </Field>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, flex: "1 1 220px", minWidth: 0 }}>
      <span className="t-meta">{label}</span>
      {children}
    </label>
  );
}
